import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { getFileUrl } from "../../core/config/api.js";
import "./reference-select.scss";

/**
 * ReferenceSelect — a dropdown that carries thumbnails, and searches the whole
 * catalogue.
 *
 * A native <option> cannot hold an image in Chrome or Safari, so this is a
 * hand-built ARIA combobox/listbox. It reads as a form control next to the
 * native selects beside it (same height, border, radius, focus ring), and the
 * panel is the rich candidate list.
 *
 * TWO sources, in one list:
 *   1. What dictation matched — scored candidates, then the unscored SQL
 *      fallback under its own heading. This is what the panel shows at rest.
 *   2. Anything else in the catalogue, via the search box at the top of the
 *      panel. Dictation only ever returns what it thought it heard; a rep who
 *      knows the reference should not have to re-record to reach it.
 *
 * Searched rows are deliberately thinner than dictated ones. POST
 * /api/products/search returns id / reference_number / brand_name / model_name /
 * image_id and NOT the EAV attributes (case diameter, dial colour, case
 * material), which are only on GET /api/products/{id}. Fetching those per row
 * would be one request per keystroke per result, so searched rows fall back to
 * brand · model on their detail line — `detailOf` already does exactly that for
 * a candidate with no attributes.
 *
 * The phone case is why this is safe: under 768px the panel opens as a
 * full-width BOTTOM SHEET, matching the dictation sheet pattern, so it scrolls
 * its own body natively instead of fighting the page — which is what ruled out
 * an anchored popover at 320px.
 *
 * Nothing is ever preselected. The top candidate is shown first because it
 * scored highest, not because it has been chosen.
 *
 * @param {Array}  candidates  scored matches
 * @param {Array}  loose       source:"search" fallbacks — shown, never as peers
 * @param {object} value       the selected candidate, or null
 */

// Dial colour, then case material, then diameter — "Blue · Ceramic, Yellow
// Gold · 40.00 MM". All three are string|null, so only the parts present are
// joined: a missing one must never leave a stray separator or the word "null".
const detailOf = (candidate) =>
  [candidate.dialColor, candidate.caseMaterial, candidate.caseDiameter]
    .filter(Boolean)
    .join(" · ") || [candidate.brand, candidate.model].filter(Boolean).join(" ");

const Thumb = ({ candidate }) => (
  <span className="rc-refsel__thumb">
    {candidate.imageId ? (
      <img src={getFileUrl(candidate.imageId)} alt="" loading="lazy" />
    ) : (
      <span className="rc-refsel__thumb-empty" aria-hidden="true">—</span>
    )}
  </span>
);

const SEARCH_LIMIT = 25;
const DEBOUNCE_MS = 250;

/**
 * A search hit, in the same shape the option renderer already takes. `searched`
 * marks the group it belongs to; the EAV attributes are null by necessity (see
 * the note above), which `detailOf` handles by falling back to brand · model.
 */
const toSearched = (product, index) => ({
  key: `s-${product?.id ?? "x"}-${index}`,
  id: product?.id ?? null,
  score: null,
  source: "catalogue",
  loose: false,
  searched: true,
  reference: product?.reference_number ?? "",
  brand: product?.brand_name ?? product?.brand?.name ?? "",
  model: product?.model_name ?? product?.name ?? product?.model?.name ?? "",
  brandId: product?.brand_id ?? null,
  modelId: product?.model_id ?? null,
  categoryId: product?.category_id ?? null,
  caseMaterial: product?.case_material ?? null,
  caseDiameter: product?.case_diameter ?? null,
  dialColor: product?.dial_color ?? null,
  imageId: product?.image_id > 0 ? product.image_id : (product?.images?.[0]?.id ?? null),
  imageIds: product?.image_id > 0 ? [product.image_id] : [],
});

const ReferenceSelect = ({ id, candidates = [], loose = [], value, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const listId = `${id}-listbox`;
  const optionId = (i) => `${id}-opt-${i}`;

  const term = query.trim();
  // Searching REPLACES the dictated list rather than appending to it: the rep
  // has typed a specific thing, and burying it under matches for something they
  // are no longer looking at is the opposite of helpful.
  const options = useMemo(
    () => (term ? results : [...candidates, ...loose]),
    [term, results, candidates, loose]
  );
  const firstLooseIndex = term ? -1 : candidates.length;

  // Debounced, and the previous request is aborted — every keystroke otherwise
  // races, and the slowest response wins whichever term it belonged to.
  useEffect(() => {
    if (!open || !term) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      axios
        .post(
          "/api/products/search",
          {
            list: "default",
            query: term,
            filters: { query: term },
            paging: { offset: 0, limit: SEARCH_LIMIT },
          },
          { signal: controller.signal }
        )
        .then(({ data }) => {
          setResults((data?.items ?? []).map(toSearched).filter((o) => o.id !== null));
          setSearching(false);
        })
        .catch((error) => {
          // An aborted request is the NEXT keystroke doing its job, not a
          // failure — leaving `searching` true there would strand the spinner.
          if (axios.isCancel?.(error) || error?.name === "CanceledError") return;
          setResults([]);
          setSearching(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, term]);

  // Highlight the first row of whatever list is now showing. Without this a
  // stale index can point past the end of a shorter result set, and Enter would
  // commit nothing.
  useEffect(() => {
    setActive(options.length > 0 ? 0 : -1);
  }, [options]);

  // Focus moves into the search box, not the listbox: the panel's first job is
  // now typing. The listbox keeps aria-activedescendant and the input keeps the
  // combobox role, which is the standard pairing for a select-with-search.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const close = useCallback(({ refocus = true } = {}) => {
    setOpen(false);
    setActive(-1);
    setQuery("");
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
  };

  const pick = (candidate) => {
    onChange(candidate);
    close();
  };

  const onTriggerKeyDown = (event) => {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      openList();
    }
  };

  const onInputKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.min(options.length - 1, i + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case "Home":
        // Only when the caret is already at the start, or Home stops working as
        // a text-editing key inside the box the rep is typing in.
        if (event.currentTarget.selectionStart === 0) {
          event.preventDefault();
          setActive(0);
        }
        break;
      case "End":
        if (event.currentTarget.selectionStart === event.currentTarget.value.length) {
          event.preventDefault();
          setActive(options.length - 1);
        }
        break;
      case "Enter":
        event.preventDefault();
        if (options[active]) pick(options[active]);
        break;
      case "Escape":
        event.preventDefault();
        // Escape must not also close the dictation sheet behind this panel.
        event.stopPropagation();
        close();
        break;
      case "Tab":
        close({ refocus: false });
        break;
      default:
        break;
    }
  };

  const emptyText = searching
    ? "Searching…"
    : term
      ? "No references found"
      : "No references matched";

  return (
    <div className={`rc-refsel${open ? " is-open" : ""}`}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`rc-refsel__trigger${value ? "" : " is-empty"}`}
        // The trigger stays the combobox. It is a <button>, which is labelable,
        // so the field's own <label for> names it — that is how the field is
        // addressed as "Reference" rather than by its contents.
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        // No longer disabled on an empty candidate list — search is the whole
        // point: with nothing matched there is still the entire catalogue.
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKeyDown}
      >
        {value ? (
          <>
            <Thumb candidate={value} />
            <span className="rc-refsel__trigger-text">
              {value.reference || "No reference"}
            </span>
          </>
        ) : (
          <span className="rc-refsel__trigger-text">Choose the reference</span>
        )}
        <span className="rc-refsel__chevron" aria-hidden="true">▾</span>
      </button>

      {open && (
        <>
          {/* Phone only: the sheet's backdrop. Tapping it is a dismissal. */}
          <div
            className="rc-refsel__scrim"
            onClick={() => close()}
            aria-hidden="true"
          />
          <div
            className="rc-refsel__panel"
            onBlur={(e) => {
              // Closing on a click inside the panel would beat the click itself.
              if (!e.currentTarget.contains(e.relatedTarget)) close({ refocus: false });
            }}
          >
            <div className="rc-refsel__search">
              <input
                ref={inputRef}
                type="text"
                className="rc-refsel__search-input"
                // A filter textbox, NOT a second combobox: the trigger above
                // already owns that role and the field's accessible name, and
                // two comboboxes for one field is a worse story for a screen
                // reader than one. `aria-activedescendant` is valid on a
                // textbox, so arrow-key navigation is still announced while
                // focus stays in the box being typed into.
                aria-controls={listId}
                aria-activedescendant={active >= 0 ? optionId(active) : undefined}
                aria-label="Search any reference"
                placeholder="Search any reference…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
              />
            </div>

            <ul id={listId} ref={listRef} className="rc-refsel__list" role="listbox">
              {options.length === 0 ? (
                <li className="rc-refsel__empty" role="presentation">{emptyText}</li>
              ) : (
                <>
                  {term && (
                    <li className="rc-refsel__group" role="presentation">
                      {searching ? "Searching…" : "Catalogue results"}
                    </li>
                  )}
                  {options.map((candidate, i) => (
                    <React.Fragment key={candidate.key}>
                      {i === firstLooseIndex && loose.length > 0 && (
                        <li className="rc-refsel__group" role="presentation">
                          Not a confident match
                        </li>
                      )}
                      <li
                        id={optionId(i)}
                        role="option"
                        aria-selected={value?.key === candidate.key}
                        className={[
                          "rc-refsel__option",
                          i === active ? "is-active" : "",
                          value?.key === candidate.key ? "is-selected" : "",
                          candidate.loose ? "is-weak" : "",
                        ].filter(Boolean).join(" ")}
                        onMouseEnter={() => setActive(i)}
                        // The input must keep focus, so the mousedown that would
                        // move it is cancelled before it lands.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(candidate)}
                      >
                        <Thumb candidate={candidate} />
                        <span className="rc-refsel__option-text">
                          <span className="rc-refsel__option-ref">
                            {candidate.reference || "No reference"}
                          </span>
                          <span className="rc-refsel__option-meta">{detailOf(candidate)}</span>
                        </span>
                      </li>
                    </React.Fragment>
                  ))}
                </>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferenceSelect;
